import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { trackReferralLinkClick, trackPageView } from './lib/analytics';
import { setReferralCode, normalizeReferralCode, displayReferralCode } from './lib/utils';
import ToastContainer from './components/ToastContainer';
import { APP_ROUTES } from './lib/appRoutes';
import ProductRecommendPopup from './components/ProductRecommendPopup';

function RefCapture() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Support ?r= (new short form), ?ref= (legacy), and ?gs= (GoodStore2 legacy)
    const rawRef = params.get('r') || params.get('ref') || params.get('gs');

    if (rawRef) {
      const ref = normalizeReferralCode(rawRef);
      if (/^R-[A-Z0-9]{6}$/i.test(ref)) {
        const existing = localStorage.getItem('x1493_referral_code');
        if (!existing) {
          setReferralCode(ref);
        }
        trackReferralLinkClick(displayReferralCode(ref), location.pathname);

        // Clean URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.delete('r');
        url.searchParams.delete('ref');
        url.searchParams.delete('gs');
        navigate(url.pathname + url.search, { replace: true });
      }
    }
  }, [location, navigate]);

  return null;
}

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
}

// Fix trailing-slash white screen: normalize /admin/ -> /admin before Router reads URL
const initialPathname = typeof window !== 'undefined' ? window.location.pathname : '';
if (initialPathname.length > 1 && initialPathname.endsWith('/')) {
  const normalized = initialPathname.slice(0, -1) + window.location.search + window.location.hash;
  window.history.replaceState(null, '', normalized);
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <RefCapture />
      <PageViewTracker />
      <ToastContainer />
      <ProductRecommendPopup />
      <Routes>
        {APP_ROUTES.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}
      </Routes>
    </Router>
  );
}

export default App;
